import { memo, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Dialog } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/material/Grid'
import DOMPurify from 'dompurify'

import { useGetContactQuery, useGetCustomerQuery } from '@/app/services/customer'
import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useGetOrderQuery } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import {
    selectCustomerBillingAddress,
    selectCustomerDeliveryAddress,
    selectCustomerId,
    selectCustomerRequiredShippingDate,
    selectCustomerShippingOption,
} from '@/app/slices/customerSlice'
import {
    selectGuestUser,
    selectIsGuestLoggedIn,
    selectOpenGuestLoginDialog,
    setOpenGuestLoginDialog,
} from '@/app/slices/guestUserSlice'
import { setSelectedOrderPaymentStatus, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import AppToolbar from '@/common/components/AppBar/AppToolbar'
import GuestLogin from '@/common/components/GuestLogin/GuestLogin'
import { useAssignOrderToUser } from '@/common/hooks/useAssignOrderToUser'
import { useOrderNavigation } from '@/common/hooks/useOrderNavigation'
import { ORDER_STATUS_ARRAY, ORDER_USER_TYPE, Paths } from '@/common/utils'

import OrderCustomInstructions from '../order/components/OrderCustomInstructions/OrderCustomInstructions'
import OrderHeader from '../order/components/OrderHeader/OrderHeader'

import OrderCheckoutDetails from './components/OrderCheckoutDetails'
import OrderCheckoutSummary from './components/OrderCheckoutSummary'

const classes = {
    section: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
    },
}

const OrderCheckout = () => {
    const { orderId } = useParams()
    const dispatch = useDispatch()
    const { isAuthenticated } = useKindeAuth()
    const navigate = useNavigate()
    const navigation = useOrderNavigation('checkout')

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const currentContactId = useSelector(selectContactId)
    const currentCustomerId = useSelector(selectCustomerId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const openGuestLoginDialog = useSelector(selectOpenGuestLoginDialog)
    const guestUser = useSelector(selectGuestUser)
    const selectedBillingGoogleAddress = useSelector(selectCustomerBillingAddress)
    const selectedDeliveryGoogleAddress = useSelector(selectCustomerDeliveryAddress)
    const selectedShippingOption = useSelector(selectCustomerShippingOption)
    const selectedRequiredShippingDate = useSelector(selectCustomerRequiredShippingDate)

    const {
        data: order,
        isLoading,
        isSuccess,
    } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const { data: currentCustomer } = useGetCustomerQuery(
        { organisationId, customerId: currentCustomerId },
        { skip: !isAuthenticated && !currentCustomerId }
    )

    const { data: currentContact } = useGetContactQuery(
        { organisationId, contactId: currentContactId, customerId: currentCustomerId },
        { skip: !isAuthenticated && !currentContactId && !currentCustomerId }
    )

    useAssignOrderToUser(order, false)

    const isInCheckout = useMemo(() => {
        return [ORDER_STATUS_ARRAY.Issued, ORDER_STATUS_ARRAY.Draft].includes(order?.status)
    }, [order?.status])

    const defaultShippingOption = useMemo(() => {
        if (
            !storeSettings?.webStorePickupEnabled &&
            storeSettings?.webStoreDeliveryEnabled &&
            !!storeSettings?.webStoreDeliveryRegions.length
        ) {
            return 'Delivery'
        }

        return 'Pickup'
    }, [storeSettings])

    const hasCustomInstructions = useMemo(() => {
        return (
            storeSettings?.webStoreCustomOrderInstructions &&
            storeSettings?.webStoreCustomOrderInstructions.trim() !== '' &&
            DOMPurify.sanitize(storeSettings?.webStoreCustomOrderInstructions).trim() !== '<p></p>'
        )
    }, [storeSettings?.webStoreCustomOrderInstructions])

    const form = useForm({
        mode: 'all',
        defaultValues: {
            name: '',
            companyName: '',
            phone: '',
            email: '',
            billingAddress: {
                googleAddress: '',
                line1: '',
                line2: '',
                city: '',
                state: '',
                country: '',
                postcode: '',
            },
            billingCountryCode: '',
            deliveryAddress: {
                googleAddress: '',
                line1: '',
                line2: '',
                city: '',
                state: '',
                country: '',
                postcode: '',
            },
            deliveryCountryCode: '',
            useOneAddress: false,
            shippingOption: defaultShippingOption,
            requiredShippingDate: null,
            customerDeliveryProvider: '',
            customerDeliveryAccountNumber: '',
            deliveryProviderToUse: 'defaultProvider',
        },
    })

    useEffect(() => {
        if (order) {
            dispatch(setSelectedOrderPaymentStatus(order?.paymentStatus))
            dispatch(setSelectedOrderStatus(order?.status))
        }
    }, [order])

    // Re-render child components if user logs in
    useEffect(() => {
        let contactData
        let customerData

        if (isAuthenticated && currentContact && currentCustomer) {
            contactData = currentContact
            customerData = currentCustomer
        }
        if (!isAuthenticated && !order?.customerContactId && isGuestLoggedIn && guestUser) {
            contactData = guestUser
            customerData = guestUser
        }
        if (
            !isAuthenticated &&
            order?.customerContactId &&
            [ORDER_USER_TYPE.Guest].includes(order?.quoteUserType) &&
            isGuestLoggedIn &&
            guestUser
        ) {
            contactData = guestUser
            customerData = guestUser
        }

        const name = contactData?.name !== contactData?.email ? contactData?.name : ''
        const companyName = customerData?.companyName !== contactData?.name ? customerData?.companyName : ''

        form.reset({
            name: name,
            companyName: companyName,
            phone: contactData?.phone || '',
            email: contactData?.email || '',
            billingAddress: {
                googleAddress: selectedBillingGoogleAddress || '',
                line1: customerData?.billingAddress?.line1 || '',
                line2: customerData?.billingAddress?.line2 || '',
                city: customerData?.billingAddress?.city || '',
                state: customerData?.billingAddress?.state || '',
                country: customerData?.billingAddress?.country || '',
                postcode: customerData?.billingAddress?.postcode || '',
            },
            billingCountryCode: customerData?.billingCountryCode || '',
            deliveryAddress: {
                googleAddress: selectedDeliveryGoogleAddress || '',
                line1: customerData?.deliveryAddress?.line1 || '',
                line2: customerData?.deliveryAddress?.line2 || '',
                city: customerData?.deliveryAddress?.city || '',
                state: customerData?.deliveryAddress?.state || '',
                country: customerData?.deliveryAddress?.country || '',
                postcode: customerData?.deliveryAddress?.postcode || '',
            },
            deliveryCountryCode: customerData?.deliveryCountryCode || '',
            useOneAddress: customerData?.useOneAddress,
            shippingOption: selectedShippingOption || defaultShippingOption,
            requiredShippingDate: order?.requiredDateUtc || selectedRequiredShippingDate,
            customerDeliveryProvider:
                order?.customerDeliveryProvider ||
                order?.deliveryProvider ||
                storeSettings?.webStorePreferredDeliveryProvider,
            customerDeliveryAccountNumber: order?.customerDeliveryAccountNumber || '',
            deliveryProviderToUse: 'defaultProvider',
        })
    }, [isAuthenticated, isGuestLoggedIn, currentContact, currentCustomer, guestUser, order, form, storeSettings])

    const handleGuestDialogClose = () => {
        dispatch(setOpenGuestLoginDialog(null))
    }

    const handleCancelGuestDialogClose = () => {
        navigate(Paths.HOME_PATHNAME)
    }

    if (navigation) return navigation

    if (!isLoading && isSuccess && isInCheckout) {
        return (
            <>
                <AppToolbar key={`${order?.id}-toolbar`} />

                {openGuestLoginDialog ? (
                    <Dialog
                        classes={{ paper: classes.dialogPaper }}
                        maxWidth="sm"
                        open={Boolean(openGuestLoginDialog)}
                        onClose={handleGuestDialogClose}
                    >
                        <GuestLogin onCancelClose={handleCancelGuestDialogClose} />
                    </Dialog>
                ) : (
                    <>
                        <Grid
                            alignContent="flex-start"
                            component="section"
                            size={{
                                lg: 9,
                                xs: 12,
                            }}
                            spacing={3}
                            container
                        >
                            <Grid size={12}>
                                <OrderHeader key={`${order?.id}-header`} />
                            </Grid>

                            {hasCustomInstructions ? (
                                <Grid size={12}>
                                    <OrderCustomInstructions key={`${order?.id}-custom-instructions`} />
                                </Grid>
                            ) : null}

                            <Grid
                                size={12}
                                sx={classes.section}
                            >
                                <OrderCheckoutDetails
                                    customerDeliveryProviderOption={
                                        storeSettings?.webStoreCustomerDeliveryProviderOption
                                    }
                                    defaultDeliveryProvider={storeSettings?.webStorePreferredDeliveryProvider}
                                    deliveryPriceMethod={order.deliveryPricingMethod}
                                    form={form}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            component="aside"
                            size={{
                                lg: 3,
                                xs: 12,
                            }}
                            sx={[classes.section, { position: 'sticky', top: '90px' }]}
                        >
                            <OrderCheckoutSummary form={form} />
                        </Grid>
                    </>
                )}
            </>
        )
    }
}

export default memo(OrderCheckout)
